// src/modules/p2c/analytics/api/client.ts

import axios, { AxiosRequestConfig } from 'axios';
import {
  IBaseParams,
  IOverviewMetrics,
  ITrafficDataPoint,
  IPageMetrics,
  IReferrerBreakdown,
  IPageBreakdown,
  IClientBreakdown,
  IFunnelStageData,
  ITrafficDayData,
  IDeviceBreakdown,
  IApiResponse,
  IReport,
  IWebsite
} from './types';

const BASE_URL = '/analytics';

interface IRequestOptions {
  method?: AxiosRequestConfig['method'];
  path: string;
  // eslint-disable-next-line
  params?: Record<string, any>;
  body?: unknown;
}

// eslint-disable-next-line
const formatParams = (params?: Record<string, any>): Record<string, any> => {
  if (!params) return {};

  const formatted = Object.fromEntries(
    Object.entries(params).map(([key, value]) => {
      if (value instanceof Date) {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');

        return [key, `${year}-${month}-${day}`];
      }
      return [key, value];
    })
  );

  return formatted;
};

const request = async <T>({
  method = 'get',
  path,
  params = {},
  body
}: IRequestOptions): Promise<IApiResponse<T>> => {
  const url = `${BASE_URL}${path}`;

  const config: AxiosRequestConfig = {
    method,
    url,
    params: formatParams(params),
    ...(body ? { data: body } : {})
  };

  const { data } = await axios.request<IApiResponse<T>>(config);

  if (!data.success) throw new Error('API returned success: false');

  return data;
};

// Overview metrics
export const fetchOverviewMetrics = (website: string, params: IBaseParams) =>
  request<IOverviewMetrics>({
    path: `/website/${website}/report/overview`,
    method: 'get',
    params
  }).then(r => r.data);

// Traffic evolution over time
export const fetchTrafficEvolution = (website: string, params: IBaseParams & { pad: string }) =>
  request<ITrafficDataPoint[]>({
    path: `/website/${website}/report/traffic/evolution`,
    method: 'get',
    params
  }).then(r => r.data);

// Traffic heatmap
export const fetchTrafficRepartition = (website: string, params: IBaseParams) =>
  request<ITrafficDayData[]>({
    path: `/website/${website}/report/traffic/repartition`,
    method: 'get',
    params
  }).then(r => r.data);

// Page metrics
export const fetchPageMetrics = (website: string, params: IBaseParams & {
  page: number;
  order?: string;
  sort?: string;
  limit?: number;
  lookup?: string;
}) =>
  request<IPageMetrics[]>({
    path: `/website/${website}/report/pages`,
    method: 'get',
    params
  });

// Page breakdown
export const fetchPageBreakdown = (website: string, params: IBaseParams) =>
  request<IPageBreakdown[]>({
    path: `/website/${website}/report/top-pages`,
    method: 'get',
    params
  }).then(r => r.data);

// Referrer breakdown
export const fetchReferrerBreakdown = (website: string, params: IBaseParams) =>
  request<IReferrerBreakdown[]>({
    path: `/website/${website}/report/referrers`,
    method: 'get',
    params
  }).then(r => r.data);

// Client breakdown (country, browser, device, etc.)
export const fetchClientBreakdown = (website: string, params: IBaseParams) =>
  request<IClientBreakdown>({
    path: `/website/${website}/report/client`,
    method: 'get',
    params
  }).then(r => r.data);

// Device breakdown with aggregation
export const fetchDeviceBreakdown = (website: string, params: IBaseParams & { agg: 'screen' | 'browser' | 'country' }) =>
  request<IDeviceBreakdown[]>({
    path: `/website/${website}/report/client/devices`,
    method: 'get',
    params
  }).then(r => r.data);

// Custom reports
export const fetchReports = (website: string) =>
  request<IReport[]>({
    path: `/website/${website}/report/custom`,
    method: 'get',
    params: {}
  }).then(r => r.data);

export const fetchReportData = (website: string, report: string, params: IBaseParams) =>
  request<IFunnelStageData[]>({
    path: `/website/${website}/report/custom/${report}`,
    method: 'get',
    params
  }).then(r => r.data);

export const deleteReport = (website: string, report: string) =>
  request<IFunnelStageData[]>({
    path: `/website/${website}/report/custom/${report}`,
    method: 'delete',
  }).then(r => r.data);

export const createReport = (website: string, report: IReport) =>
  request<IFunnelStageData[]>({
    path: `/website/${website}/report/custom`,
    method: 'post',
    body: report
  }).then(r => r.data);

export const fetchWebsites = () =>
  request<IWebsite[]>({
    path: '/website',
    method: 'get',
    params: {}
  }).then(r => r.data);

